import React, { useEffect, useState } from "react";

import { createPortal } from "react-dom";

import { X } from "lucide-react";

export default function RepeatCustomizationModal({
  open,
  itemName = "",
  preferences = [],
  onClose,
  onChooseNew,
  onRepeat,
}) {
  // =========================================================
  // QUANTITY PER SAVED PREFERENCE
  // =========================================================

  const [preferenceQuantities, setPreferenceQuantities] = useState({});

  // =========================================================
  // RESET WHEN MODAL OPENS
  // =========================================================

  useEffect(() => {
    if (open) {
      setPreferenceQuantities({});
    }
  }, [open]);

  // =========================================================
  // BODY LOCK + ESCAPE
  // =========================================================

  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    const oldOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = oldOverflow;

      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const safePreferences = Array.isArray(preferences) ? preferences : [];

  // =========================================================
  // TOTAL SELECTED QUANTITY
  // =========================================================

  const totalSelectedQuantity = Object.values(preferenceQuantities).reduce(
    (total, quantity) => total + Number(quantity || 0),
    0,
  );

  // =========================================================
  // FORMAT SAVED PREFERENCE
  // =========================================================

  const getPreferenceLines = (preference = {}) => {
    const lines = Object.entries(preference)
      .filter(([, value]) => {
        if (value === null || value === undefined) {
          return false;
        }

        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return String(value).trim() !== "";
      })
      .map(([key, value]) => {
        const displayValue = Array.isArray(value)
          ? value.join(", ")
          : String(value);

        return {
          key,
          value: displayValue,
        };
      });

    if (lines.length === 0) {
      return [
        {
          key: "Preference",
          value: "Default customization",
        },
      ];
    }

    return lines;
  };

  // =========================================================
  // ADD FIRST QUANTITY
  // =========================================================

  const handleAddPreference = (index) => {
    setPreferenceQuantities((previous) => ({
      ...previous,
      [index]: 1,
    }));
  };

  // =========================================================
  // PLUS
  // =========================================================

  const handleIncreasePreference = (index) => {
    setPreferenceQuantities((previous) => ({
      ...previous,

      [index]: Number(previous[index] || 0) + 1,
    }));
  };

  // =========================================================
  // MINUS
  // =========================================================

  const handleDecreasePreference = (index) => {
    setPreferenceQuantities((previous) => {
      const currentQuantity = Number(previous[index] || 0);

      const nextQuantity = Math.max(0, currentQuantity - 1);

      const updated = {
        ...previous,
      };

      if (nextQuantity === 0) {
        delete updated[index];
      } else {
        updated[index] = nextQuantity;
      }

      return updated;
    });
  };

  // =========================================================
  // DONE
  // =========================================================

  const handleDone = () => {
    if (totalSelectedQuantity <= 0) {
      return;
    }

    safePreferences.forEach((preference, index) => {
      const quantity = Number(preferenceQuantities[index] || 0);

      if (quantity <= 0) {
        return;
      }

      // Same saved preference ko selected quantity ke
      // according cart me add karo.
      for (let count = 0; count < quantity; count += 1) {
        onRepeat?.(preference);
      }
    });
  };

  return createPortal(
    <div
      className="
        fixed
        inset-0
        z-[200000]

        flex
        items-center
        justify-center

        bg-black/55

        p-3

        backdrop-blur-[1px]

        sm:p-5
      "
      onClick={onClose}
    >
      <div
        className="
          flex
          max-h-[88dvh]
          w-full
          max-w-[500px]
          flex-col
          overflow-hidden

          rounded-[20px]
          bg-white

          shadow-2xl

          sm:rounded-[24px]
        "
        onClick={(event) => event.stopPropagation()}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div
          className="
            flex
            shrink-0
            items-start
            justify-between
            gap-3

            border-b
            border-gray-100

            px-4
            py-4

            sm:px-5
          "
        >
          <div className="min-w-0 flex-1">
            <p
              className="
                truncate
                text-[12px]
                font-medium
                text-gray-500

                sm:text-[13px]
              "
            >
              {itemName}
            </p>

            <h2
              className="
                mt-0.5
                text-[18px]
                font-bold
                leading-6
                text-[#172033]

                sm:text-[21px]
              "
            >
              Repeat previous customisation?
            </h2>

            <p className="mt-1 text-[11px] text-gray-500 sm:text-[12px]">
              Select quantity for a saved preference or choose a new one.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="
              flex
              h-9
              w-9
              shrink-0
              items-center
              justify-center

              rounded-full

              text-gray-500

              transition

              hover:bg-gray-100
            "
          >
            <X size={19} />
          </button>
        </div>

        {/* ================================================= */}
        {/* SAVED PREFERENCES */}
        {/* ================================================= */}

        <div
          className="
            min-h-0
            flex-1
            space-y-2.5
            overflow-y-auto

            bg-[#f8fafc]

            px-4
            py-4

            sm:px-5
          "
        >
          {safePreferences.map((preference, index) => {
            const lines = getPreferenceLines(preference);

            const quantity = Number(preferenceQuantities[index] || 0);

            return (
              <div
                key={`saved-preference-${index}`}
                className="
                  w-full
                  rounded-[14px]

                  border
                  border-gray-200

                  bg-white

                  px-4
                  py-3

                  transition

                  hover:border-blue-200
                "
              >
                {/* ========================================= */}
                {/* TITLE + ADD / COUNTER */}
                {/* ========================================= */}

                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 text-[12px] font-bold text-gray-800 sm:text-[13px]">
                    Preference {index + 1}
                  </span>

                  {quantity === 0 ? (
                    // =======================================
                    // ADD
                    // =======================================

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();

                        handleAddPreference(index);
                      }}
                      className="
                        flex
                        h-8
                        min-w-[62px]
                        shrink-0
                        items-center
                        justify-center

                        rounded-full

                        bg-[#2454e6]

                        px-3

                        text-[10px]
                        font-bold
                        text-white

                        transition

                        hover:bg-[#1d4ed8]
                        active:scale-95

                        sm:h-9
                        sm:min-w-[70px]
                        sm:px-4
                        sm:text-[11px]
                      "
                    >
                      ADD
                    </button>
                  ) : (
                    // =======================================
                    // QUANTITY COUNTER
                    // =======================================

                    <div
                      className="
                        flex
                        h-8
                        shrink-0
                        items-center

                        overflow-hidden
                        rounded-full

                        bg-[#2454e6]

                        text-white

                        sm:h-9
                      "
                    >
                      {/* MINUS */}

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();

                          handleDecreasePreference(index);
                        }}
                        className="
                          flex
                          h-full
                          w-8
                          items-center
                          justify-center

                          text-[16px]
                          font-medium

                          transition

                          hover:bg-blue-700

                          sm:w-9
                        "
                      >
                        −
                      </button>

                      {/* COUNT */}

                      <span
                        className="
                          min-w-[24px]
                          text-center

                          text-[11px]
                          font-bold

                          sm:min-w-[28px]
                          sm:text-[12px]
                        "
                      >
                        {quantity}
                      </span>

                      {/* PLUS */}

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();

                          handleIncreasePreference(index);
                        }}
                        className="
                          flex
                          h-full
                          w-8
                          items-center
                          justify-center

                          text-[16px]
                          font-medium

                          transition

                          hover:bg-blue-700

                          sm:w-9
                        "
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>

                {/* ========================================= */}
                {/* PREFERENCE DETAILS */}
                {/* ========================================= */}

                <div className="mt-2 space-y-1">
                  {lines.map((line) => (
                    <p
                      key={`${index}-${line.key}`}
                      className="
                        break-words
                        text-[11px]
                        leading-5
                        text-gray-600

                        sm:text-[12px]
                      "
                    >
                      <span className="font-medium text-gray-700">
                        {line.key}:
                      </span>{" "}
                      {line.value}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* ================================================= */}
        {/* FOOTER - ONLY 2 BUTTONS */}
        {/* ================================================= */}

        <div
          className="
            shrink-0

            border-t
            border-gray-100

            bg-white

            p-4

            sm:p-5
          "
        >
          <div className="grid grid-cols-2 gap-3">
            {/* ============================================= */}
            {/* I'LL CHOOSE */}
            {/* ============================================= */}

            <button
              type="button"
              onClick={onChooseNew}
              className="
                flex
                h-11
                min-w-0
                items-center
                justify-center

                rounded-full

                border
                border-[#2454e6]

                bg-white

                px-3

                text-center
                text-[12px]
                font-bold
                text-[#2454e6]

                transition

                hover:bg-blue-50

                sm:h-12
                sm:px-4
                sm:text-[14px]
              "
            >
              I'll choose
            </button>

            {/* ============================================= */}
            {/* DONE */}
            {/* ============================================= */}

            <button
              type="button"
              disabled={totalSelectedQuantity <= 0}
              onClick={handleDone}
              className="
                flex
                h-11
                min-w-0
                items-center
                justify-center

                rounded-full

                bg-[#2454e6]

                px-4

                text-[13px]
                font-bold
                text-white

                transition

                hover:bg-[#1d4ed8]
                active:scale-95

                disabled:cursor-not-allowed
                disabled:bg-gray-300
                disabled:text-gray-500
                disabled:hover:bg-gray-300
                disabled:active:scale-100

                sm:h-12
                sm:text-[14px]
              "
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
